import React, { useEffect } from 'react';

import { useUnitsAfterState, useUnitsBeforeState } from 'app-components/hooks/investment-forms';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import InvestmentTransactionBaseForm from './InvestmentTransactionBaseForm';
import { makeNumericKeyDownHandler, sanitizeNumericValue } from './numericInputHelpers';
import type { InvestmentTransactionFormikProps } from './types';

const handleIntegerKeyDown = makeNumericKeyDownHandler({ allowDecimal: false });
const handleNumericKeyDown = makeNumericKeyDownHandler();

const InvestmentTransactionStockSplitForm = (props: InvestmentTransactionFormikProps) => {
  const { formik } = props;

  useEffect(() => {
    formik.registerField('splitNumerator', {
      validate: (value: number | null | undefined) =>
        typeof value === 'number' && value ? undefined : 'use valid number',
    });
    formik.registerField('splitDenominator', {
      validate: (value: number | null | undefined) =>
        typeof value === 'number' && value ? undefined : 'use valid number',
    });
    return () => {
      formik.unregisterField('splitNumerator');
      formik.unregisterField('splitDenominator');
    };
  }, [formik]);

  const [unitsBefore, setUnitsBefore] = useUnitsBeforeState(formik);
  const [unitsAfter, setUnitsAfter] = useUnitsAfterState(formik);

  return (
    <>
      <InvestmentTransactionBaseForm formik={formik} />

      <Box display='flex' gap={3} flexDirection='column'>
        <Box display='flex' alignItems='center' gap={1}>
          <TextField
            id='split-numerator'
            label='Post Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'numeric' }}
            sx={{ minWidth: 110 }}
            value={formik.values.splitNumerator}
            onKeyDown={handleIntegerKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const sanitized = sanitizeNumericValue(event.target.value, { allowDecimal: false });
              formik.setFieldValue('splitNumerator', sanitized ? Math.abs(Number(sanitized)) : null);
            }}
            error={Boolean(formik.submitCount > 0 && formik.errors.splitNumerator)}
            helperText={(formik.submitCount > 0 && formik.errors.splitNumerator) || ''}
          />
          :
          <TextField
            id='split-denominator'
            label='Pre Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'numeric' }}
            sx={{ minWidth: 110 }}
            value={formik.values.splitDenominator}
            onKeyDown={handleIntegerKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const sanitized = sanitizeNumericValue(event.target.value, { allowDecimal: false });
              formik.setFieldValue('splitDenominator', sanitized ? Math.abs(Number(sanitized)) : null);
            }}
            error={Boolean(formik.submitCount > 0 && formik.errors.splitDenominator)}
            helperText={(formik.submitCount > 0 && formik.errors.splitDenominator) || ''}
          />
        </Box>

        <Box display='flex' alignItems='center' gap={1}>
          <TextField
            id='units-after'
            label='Units After Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'decimal' }}
            sx={{ minWidth: 110 }}
            disabled={formik.values.splitNumerator === formik.values.splitDenominator}
            value={unitsAfter}
            onKeyDown={handleNumericKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const sanitized = sanitizeNumericValue(event.target.value);
              setUnitsAfter(sanitized === '' ? null : Number(sanitized));
            }}
          />
          -
          <TextField
            id='units-before'
            label='Units Before Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'decimal' }}
            sx={{ minWidth: 110 }}
            disabled={formik.values.splitNumerator === formik.values.splitDenominator}
            value={unitsBefore}
            onKeyDown={handleNumericKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const sanitized = sanitizeNumericValue(event.target.value);
              setUnitsBefore(sanitized === '' ? null : Number(sanitized));
            }}
          />
          =
          <TextField
            id='units'
            label='Shares adjustment'
            type='number'
            disabled
            sx={{ minWidth: 110 }}
            value={
              formik.values.splitNumerator !== formik.values.splitDenominator && formik.values.units != null
                ? Math.abs(formik.values.units)
                : ''
            }
          />
        </Box>
      </Box>
    </>
  );
};

export default InvestmentTransactionStockSplitForm;
